<template>
  <div class="admin-page">
    <admin-page-header
      eyebrow="İletişim"
      title="Bülten Aboneleri"
      description="Bülten abonelerini, kayıt tarihlerini ve abonelik durumlarını görüntüleyin."
    >
      <template #actions>
        <v-btn
          outlined
          color="primary"
          :loading="exporting"
          :disabled="loading || !total"
          @click="exportCsv"
        >
          CSV İndir
        </v-btn>
      </template>
    </admin-page-header>
    <v-alert
      v-if="error"
      dense
      outlined
      type="error"
      class="mb-4"
      role="alert"
    >
      {{ error }}
    </v-alert>
    <v-card
      class="admin-table-card"
      outlined
    >
      <v-card-text>
        <v-row align="center">
          <v-col
            cols="12"
            md="6"
          >
            <v-text-field
              v-model="query"
              label="Tam e-posta adresi ile ara"
              placeholder="ornek@eposta.com"
              maxlength="254"
              clearable
              outlined
              dense
              hide-details
              @keyup.enter="applyFilters"
            />
          </v-col>
          <v-col
            cols="12"
            md="4"
          >
            <v-select
              v-model="status"
              :items="statuses"
              label="Abonelik durumu"
              clearable
              outlined
              dense
              hide-details
              @change="applyFilters"
            />
          </v-col>
          <v-col
            cols="12"
            md="2"
          >
            <v-btn
              color="primary"
              :loading="loading"
              @click="applyFilters"
            >
              Ara / Yenile
            </v-btn>
          </v-col>
        </v-row>
        <p class="text-caption mb-0 mt-4">
          {{ total.toLocaleString('tr-TR') }} kayıt · CSV, uyguladığınız filtrelere göre indirilir.
        </p>
      </v-card-text>
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        :options.sync="options"
        :server-items-length="total"
        :footer-props="{ 'items-per-page-options': [25, 50, 100], 'items-per-page-text': 'Sayfa başına' }"
        no-data-text="Bu filtrelere uygun bülten abonesi bulunamadı."
        loading-text="Bülten aboneleri yükleniyor…"
        disable-sort
        class="admin-data-table elevation-0"
      >
        <template #[`item.status`]="{ item }">
          <v-chip
            small
            outlined
            :color="item.status === 'ACTIVE' ? 'success' : 'secondary'"
          >
            {{ item.status === 'ACTIVE' ? 'Aktif' : 'Kapalı' }}
          </v-chip>
        </template>
        <template #[`item.closureReason`]="{ item }">
          {{ item.closureReason || '-' }}
        </template>
        <template #[`item.createdAt`]="{ item }">
          {{ formatDate(item.createdAt) }}
        </template>
        <template #[`item.closedAt`]="{ item }">
          {{ formatDate(item.closedAt) }}
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>
<script>
import axios from 'axios'
import AdminPageHeader from '@/components/admin/AdminPageHeader.vue'
import { getAccessToken } from '@/utils/panelSession'
import { formatAdminDate } from '@/utils/adminDate'

export default {
  components: { AdminPageHeader },
  data: () => ({
    items: [],
    total: 0,
    loading: false,
    exporting: false,
    error: '',
    query: '',
    status: null,
    appliedQuery: '',
    appliedStatus: null,
    options: { page: 1, itemsPerPage: 25 },
    requestSequence: 0,
    statuses: [
      { text: 'Aktif', value: 'ACTIVE' },
      { text: 'Kendi isteğiyle ayrılan', value: 'UNSUBSCRIBED' },
      { text: 'Kalıcı teslimat hatası', value: 'HARD_BOUNCE' },
      { text: 'Art arda 3 geçici teslimat hatası', value: 'SOFT_BOUNCES' },
      { text: 'Spam şikayeti', value: 'COMPLAINT' },
    ],
    headers: [
      { text: 'E-posta', value: 'email' },
      { text: 'Kayıt tarihi', value: 'createdAt' },
      { text: 'Durum', value: 'status' },
      { text: 'Kapanma nedeni', value: 'closureReason' },
      { text: 'Kapanma tarihi', value: 'closedAt' },
    ],
  }),
  computed: {
    apiBase() { return `${process.env.VUE_APP_BASE_API_URL}admin/newsletter/subscribers` },
    requestOptions() { return { headers: { Authorization: `Bearer ${getAccessToken()}` } } },
  },
  watch: { options: { deep: true, handler() { this.load() } } },
  mounted() { this.load() },
  beforeDestroy() { this.requestSequence += 1 },
  methods: {
    formatDate: formatAdminDate,
    filters() { return { query: this.appliedQuery || undefined, status: this.appliedStatus || undefined } },
    errorText(error) {
      const status = error.response && error.response.status
      if (status === 400) return 'Aramak için tam bir e-posta adresi girin.'
      if (status === 401) return 'Oturumunuz sona erdi. Lütfen tekrar giriş yapın.'
      if (status === 403) return 'Bülten abonelerini görüntülemek için yönetici yetkisi gerekir.'
      if (status === 413) return 'CSV için filtreyi daraltın; en fazla 50.000 kayıt indirilebilir.'

      return 'İşlem tamamlanamadı. Lütfen tekrar deneyin.'
    },
    applyFilters() {
      this.appliedQuery = (this.query || '').trim()
      this.appliedStatus = this.status
      if (this.options.page !== 1) this.options = { ...this.options, page: 1 }
      else this.load()
    },
    async load() {
      this.requestSequence += 1
      const sequence = this.requestSequence
      this.loading = true
      this.error = ''
      try {
        const response = await axios.get(this.apiBase, {
          ...this.requestOptions,
          params: { ...this.filters(), page: this.options.page - 1, size: this.options.itemsPerPage },
        })
        if (sequence !== this.requestSequence) return
        this.items = response.data.content || []
        this.total = response.data.totalElements || 0
      } catch (error) {
        if (sequence !== this.requestSequence) return
        this.items = []
        this.total = 0
        this.error = this.errorText(error)
      } finally {
        if (sequence === this.requestSequence) this.loading = false
      }
    },
    async exportCsv() {
      if (this.exporting) return
      this.exporting = true
      this.error = ''
      try {
        const response = await axios.get(`${this.apiBase}/export`, {
          ...this.requestOptions, params: this.filters(), responseType: 'blob',
        })
        const url = URL.createObjectURL(response.data)
        const link = document.createElement('a')
        link.href = url
        link.download = 'adverport-bulten-aboneleri.csv'
        document.body.appendChild(link)
        link.click()
        link.remove()
        setTimeout(() => URL.revokeObjectURL(url), 1000)
      } catch (error) {
        this.error = this.errorText(error)
      } finally {
        this.exporting = false
      }
    },
  },
}
</script>
